import { useEffect, useMemo } from 'react';
import {
  CellEditingStartedEvent,
  GridApi,
  GridOptions,
} from 'ag-grid-community';
import { GroupingColumn, Row, UseControlledAGGridProps } from '../types';

export const useEditable = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi<Row<RowData, RowId>> | null,
  groupingColumn: GroupingColumn<RowData, RowId> | undefined
) => {
  const editable = props.editable;
  const editableEnabled = editable !== undefined;
  const groupingColumnId = groupingColumn ? groupingColumn.id : null;

  const editedCoordinate = useMemo(() => {
    if (!editable) {
      return null;
    }
    return editable.coordinate;
  }, [editable]);

  useEffect(() => {
    if (!api || !editableEnabled) {
      return;
    }

    if (editedCoordinate === null) {
      api.stopEditing();
      return;
    }

    const { columnId, rowId } = editedCoordinate || {};
    const rowIndex = api.getRowNode(rowId)?.rowIndex;
    const currentlyEditing = api.getEditingCells();

    // Map the grouping column ID to AG Grid's auto-generated column ID
    const agGridColumnId =
      columnId === groupingColumnId ? 'ag-Grid-AutoColumn' : columnId;

    if (
      typeof rowIndex !== 'number' ||
      (currentlyEditing.length === 1 &&
        currentlyEditing[0].rowIndex === rowIndex &&
        currentlyEditing[0].colId === agGridColumnId)
    ) {
      return;
    }

    api.startEditingCell({ rowIndex, colKey: agGridColumnId });
  }, [api, editableEnabled, editedCoordinate, groupingColumnId]);

  const editGridProps: GridOptions<Row<RowData, RowId>> | undefined = editable
    ? {
        stopEditingWhenCellsLoseFocus: true,
        /**
         * This is called by AG-Grid when a cell starts editing, and cannot be
         * opted out of. Always update your state accordingly.
         */
        onCellEditingStarted: (
          event: CellEditingStartedEvent<Row<RowData, RowId>, unknown>
        ) => {
          const rowId = event.node.data?.id;
          const columnId = event.column.getId();
          if (!rowId || !columnId) {
            return;
          }
          props.editable?.onSync({ columnId, rowId });
        },
        /**
         * This is called by AG-Grid when a cell stops editing, and cannot be
         * opted out of. Always update your state accordingly.
         */
        onCellEditingStopped: () => {
          props.editable?.onSync(null);
        },
      }
    : undefined;

  return { editGridProps };
};
